import { isPoleKind, cameraMountKind, mountingProfile, ceilingClearance } from './cameraMounts.js';
import { isPoleObject, poleDiameter } from './poles.js';
import { presentInScene } from './sceneObjects.js';
import { sceneWalls } from './siteGeometry.js';
import { environmentAt, outdoorReady } from './siteAreas.js';
import { blocked } from './geometry.js';
import { RAD, DEG, norm360 } from './constants.js';

export const poleOffset = pole => poleDiameter(pole) * Math.SQRT2 / 2 + 0.3;

/** Only an explicitly placed exterior pole is a support. The bracket bearing
 * is stored separately from the camera aim, so aiming cannot move its mount. */
export function poleCameraPose(S, camera, model = S.models.find(m => m.id === camera.modelId)) {
  const kind = cameraMountKind(model, camera), pole = S.obstacles?.find(o => o.id === camera.mountPoleId && isPoleObject(o));
  if (!isPoleKind(kind) || !mountingProfile(model).kinds.includes(kind) || !outdoorReady(model) || !pole || !presentInScene(pole, S.sceneState) || pole.transparent || environmentAt(S, pole.x, pole.y) !== 'outdoor') return null;
  const bottom = pole.bottom || 0, clearance = kind === 'pole-pendant' ? ceilingClearance(model) + 0.1 : 0.2;
  if (!(camera.h >= bottom + 0.4 && camera.h + clearance <= bottom + pole.height)) return null;
  const mountAzimuth = norm360(camera.mountAzimuth ?? camera.pan ?? 0), a = mountAzimuth * RAD, radius = poleOffset(pole);
  const pose = { ...camera, mountKind: kind, mountAzimuth, x: pole.x + radius * Math.cos(a), y: pole.y + radius * Math.sin(a), mountSurface: '', mountDrop: 0 };
  const pan = (pose.pan ?? mountAzimuth) * RAD;
  if (Math.cos(pan - a) < 0.05) return null;
  const walls = sceneWalls(S), anchor = poleDiameter(pole) * Math.SQRT2 / 2 + 0.01;
  if (blocked(pole.x + anchor * Math.cos(a), pole.y + anchor * Math.sin(a), pose.h, pose.x, pose.y, pose.h, walls) ||
    blocked(pose.x, pose.y, pose.h, pose.x + Math.cos(pan) * 0.75, pose.y + Math.sin(pan) * 0.75, pose.h, walls)) return null;
  return pose;
}

export function findPoleMount(S, camera) {
  const pose = poleCameraPose(S, camera);
  if (!pose || Math.hypot(pose.x - camera.x, pose.y - camera.y) > 0.025) return null;
  const pole = S.obstacles.find(o => o.id === pose.mountPoleId);
  return { pole, x: pole.x, y: pole.y, height: (pole.bottom || 0) + pole.height, radius: poleDiameter(pole) / 2 };
}

export function snapPoleCamera(S, point, model, camera = {}, maxDistance = 1.5) {
  const poles = (S.obstacles || []).filter(isPoleObject).map(pole => ({ pole, distance: Math.hypot(point.x - pole.x, point.y - pole.y) }));
  poles.sort((a, b) => a.distance - b.distance);
  for (const { pole, distance } of poles) {
    if (distance > maxDistance || point.poleId && pole.id !== point.poleId) continue;
    const mountAzimuth = distance < 0.03 ? camera.mountAzimuth ?? camera.pan ?? 0 : norm360(Math.atan2(point.y - pole.y, point.x - pole.x) * DEG);
    const base = { ...camera, modelId: model.id, mountPoleId: pole.id, mountAzimuth, h: camera.h ?? 3, pan: camera.pan ?? mountAzimuth };
    const pose = poleCameraPose(S, base, model) || poleCameraPose(S, { ...base, pan: mountAzimuth }, model);
    if (pose) return pose;
  }
  return null;
}

// Moving/resizing a support carries its attached hardware. Invalid or removed
// supports retain cameras for explicit review instead of silently deleting them.
export function syncPoleCameras(S) {
  for (const plan of [S, ...(S.floors || []).map(f => f.plan)]) for (const camera of plan.cams) {
    if (!camera.mountPoleId) continue;
    const pose = poleCameraPose(plan, camera);
    if (pose) Object.assign(camera, pose);
  }
}
